<template>
  <Drawer :on-close="closePanel">
    <template #content>
      <div class="w-full flex flex-col-reverse gap-3 md:flex-col !pb-3 md:!pb-0">
        <TabGroup>
          <TabList class="flex space-x-2 !pl-3 md:!pl-0 md:!pt-4 md:!pb-2 justify-center md:justify-start">
            <Tab v-slot="{ selected }" as="template">
              <TabButton class="min-w-[5rem]" :selected="selected">{{ I18n.t("editor.photo") }}</TabButton>
            </Tab>
            <Tab v-slot="{ selected }" as="template">
              <TabButton class="min-w-[5rem]" :selected="selected">{{ I18n.t("editor.color") }}</TabButton>
            </Tab>
            <Tab v-slot="{ selected }" as="template">
              <TabButton class="min-w-[5rem]" :selected="selected">{{ I18n.t("editor.blur") }}</TabButton>
            </Tab>
          </TabList>

          <TabPanels>
            <!-- Start Photo -->
            <TabPanel>
              <!-- Start Desktop -->
              <div class="hidden md:grid md:grid-cols-4 gap-2 overflow-scroll hide-scrollbars sm:h-64 md:!pb-4">
                <PhotoTiles :selected-image-state="props.selectedImageState" :available-backgrounds="availableBackgrounds" :is-loading="isLoading" />
              </div>
              <div class="hidden md:block shadow-fold w-full h-2"></div>
              <!-- End Desktop -->
              <!-- Start Mobile -->
              <div class="flex md:hidden gap-2 overflow-scroll hide-scrollbars px-3">
                <PhotoTilesMobile :selected-image-state="props.selectedImageState" :available-backgrounds="availableBackgrounds" :is-loading="isLoading" />
              </div>
              <!-- End Mobile -->
            </TabPanel>
            <!-- End Photo -->

            <!-- Start Color -->
            <TabPanel>
              <!-- Start Desktop -->
              <div class="hidden md:grid grid-cols-6 gap-2 overflow-scroll hide-scrollbars h-64 !pb-4 !px-0">
                <ColorTiles :selected-image-state="props.selectedImageState" :colors="availableColors" />
              </div>
              <div class="hidden md:block shadow-fold w-full h-2"></div>
              <!-- End Desktop -->
              <!-- Start Mobile -->
              <div class="flex md:hidden gap-2 overflow-scroll hide-scrollbars px-3 md:!px-0">
                <ColorTilesMobile :selected-image-state="props.selectedImageState" :colors="availableColors" />
              </div>
              <!-- End Mobile -->
            </TabPanel>
            <!-- End Color -->

            <!-- Start Blur -->
            <TabPanel>
              <div class="flex flex-col gap-6 overflow-scroll hide-scrollbars h-32 md:h-64 !px-4 !pt-2 md:!px-0 md:!pb-4">
                <div class="flex items-center gap-2">
                  <Toggle :name="I18n.t('editor.blur_background')" :model-value="props.selectedImageState.isBackgroundBlurEnabled" @update:modelValue="updateBlurState" />
                  <span class="text-sm text-typo">{{ I18n.t("editor.blur_background") }}</span>
                </div>
                <RangeSlider :value="props.selectedImageState?.blurRadius || 10" @slider-value-changed="changeBlurRadius" :label="I18n.t('editor.blur_amount')" :disabled="!props.selectedImageState.isBackgroundBlurEnabled" :selected-image-state="props.selectedImageState" :min="0" :max="25" :step="1" :on-mousedown="cacheState" :on-touchstart="cacheState" :on-mouseup="applyBlur" :on-touchend="applyBlur" />
              </div>
              <!-- Some height to compensate the missing shadow-fold -->
              <div class="w-full h-2"></div>
            </TabPanel>
            <!-- End Blur -->
          </TabPanels>
        </TabGroup>
      </div>
    </template>

    <template #mobile-actions>
      <TextButton variant="secondary" @click="reset">{{ I18n.t("editor.reset") }}</TextButton>
      <TextButton @click="closePanel">{{ I18n.t("editor.done") }}</TextButton>
    </template>

    <template #actions>
      <Button :full-width="true" variant="secondary" @click="reset">{{ I18n.t("editor.reset") }}</Button>
      <Button :full-width="true" @click="closePanel">{{ I18n.t("editor.done") }}</Button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import "@/src/i18n";

import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";

import { Button, TabButton, Toggle, Drawer, TextButton } from "prism";

import ColorTiles from "../prism/color_tiles.vue";
import ColorTilesMobile from "../prism/color_tiles_mobile.vue";
import PhotoTiles from "../prism/photo_tiles.vue";
import PhotoTilesMobile from "../prism/photo_tiles_mobile.vue";
import RangeSlider from "../prism/range_slider.vue";
import loadSuggestions from "../../src/image_suggestions";
import { onMounted, ref, inject } from "vue";
import Client from "@/modules/internal_api/client";
import { Background } from "@/modules/internal_api/image";
import { useEditorStore } from "@/stores/editor_store";
import { rbgEditorApplyBackgroundImageV100, rbgEditorApplyBlurV100, rbgEditorUploadBackgroundImageV100 } from "kaleido-event-tracker";

const I18n = inject("I18n");

interface AddBackgroundProps {
  foregroundType: string;
  selectedImageState: any;
}

const props = defineProps<AddBackgroundProps>();
const store = useEditorStore();
const availableColors = ref<string[]>(["#f44336", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5", "#2196f3", "#03a9f4", "#00bcd4", "#009688", "#4caf50", "#8bc34a", "#cddc39", "#ffeb3b", "#ffc107", "#ff9800", "#ff5722", "#795548", "#9e9e9e", "#607d8b", "#000000"]);
const availableBackgrounds = ref<Background[]>([]);
const isLoading = ref<boolean>(false);
const cachedState = ref<any>(null);
const nextBlurRadius = ref<number>(0);

onMounted(async () => {
  loadRelevantBackgrounds();
  nextBlurRadius.value = props.selectedImageState.blurRadius;
});

const loadRelevantBackgrounds = async () => {
  isLoading.value = true;
  const relevantBackgrounds = await loadSuggestions(props.foregroundType);
  availableBackgrounds.value = relevantBackgrounds;
  isLoading.value = false;
};

const updateBlurState = (value: boolean): void => {
  props.selectedImageState.withSnapshot(() => {
    props.selectedImageState.setBackgroundBlurEnabled(value);
  });

  const eventBlurRadius = value === true ? props.selectedImageState.blurRadius : 0;
  rbgEditorApplyBlurV100({ image_id: store.selectedImage.meta.id, blur_value: eventBlurRadius });
};

const cacheState = () => {
  const clonedState = JSON.parse(JSON.stringify(props.selectedImageState.current));
  cachedState.value = clonedState;
};

const applyBlur = () => {
  props.selectedImageState.setBlurRadius(nextBlurRadius.value);
  props.selectedImageState.snapshotWithState(cachedState.value);
  props.selectedImageState.persist();
  rbgEditorApplyBlurV100({ image_id: store.selectedImage.meta.id, blur_value: props.selectedImageState.blurRadius });
};

const emit = defineEmits<{
  (e: "closePanel"): void;
  (e: "reset"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const reset = () => {
  emit("reset");
};

const changeBlurRadius = (blurRadius) => {
  const parsedBlurRadius = parseInt(blurRadius, 10);
  nextBlurRadius.value = parsedBlurRadius;
};

const uploadBackgroundFile = async (file: File) => {
  const result = await Client.uploadBackgroundToBucket(store.selectedImage, file);

  if (result.success) {
    props.selectedImageState.withSnapshot(() => {
      props.selectedImageState.setSelectedBackgroundColor(undefined);
      props.selectedImageState.setUploadedBackgroundPhotoUrl(result.url);
      props.selectedImageState.setSelectedBackgroundPhotoUrl(result.url);
    });

    rbgEditorUploadBackgroundImageV100({ image_id: store.selectedImage.meta.id });
    rbgEditorApplyBackgroundImageV100({ image_id: store.selectedImage.meta.id, background_image_id: result.url });
  } else {
    console.error("Upload to bucket failed", result);
  }
};
window.uploadBackgroundFile = uploadBackgroundFile;
</script>
